import { useState, useEffect } from 'react';
import { 
  Users, 
  Plane, 
  FileText, 
  Activity,
  TrendingUp,
  UserCheck,
  AlertTriangle
} from 'lucide-react';
import api from '../../services/api';

interface Stats {
  users: {
    total: number;
    active: number;
  };
  missions: {
    total: number;
    active: number;
  };
  aircraft: {
    total: number;
    available: number;
  };
}

export default function SystemStats() {
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await api.get('/admin/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="p-6">
        <div className="text-center text-gray-500">Failed to load statistics</div>
      </div>
    );
  }

  const statCards = [
    {
      title: 'Total Users',
      value: stats.users.total,
      subValue: `${stats.users.active} active`,
      icon: Users,
      color: 'bg-blue-500',
      bgColor: 'bg-blue-50',
      textColor: 'text-blue-600'
    },
    {
      title: 'Total Missions',
      value: stats.missions.total,
      subValue: `${stats.missions.active} active`,
      icon: FileText,
      color: 'bg-green-500',
      bgColor: 'bg-green-50',
      textColor: 'text-green-600'
    },
    {
      title: 'Aircraft Fleet',
      value: stats.aircraft.total,
      subValue: `${stats.aircraft.available} available`,
      icon: Plane,
      color: 'bg-purple-500',
      bgColor: 'bg-purple-50',
      textColor: 'text-purple-600'
    }
  ];

  const utilizationRate = stats.aircraft.total > 0 
    ? Math.round((stats.aircraft.available / stats.aircraft.total) * 100)
    : 0;

  return (
    <div className="p-6">
      <h2 className="text-lg font-medium text-gray-900 mb-6">System Overview</h2>

      {/* Stat Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <div key={index} className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                  <p className="text-2xl font-semibold text-gray-900 mt-1">
                    {stat.value.toLocaleString()}
                  </p>
                  <p className="text-sm text-gray-500 mt-1">{stat.subValue}</p>
                </div>
                <div className={`${stat.bgColor} rounded-full p-3`}>
                  <Icon className={`h-6 w-6 ${stat.textColor}`} />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Additional Insights */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* User Activity */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-base font-medium text-gray-900 mb-4 flex items-center">
            <UserCheck className="h-5 w-5 mr-2 text-gray-400" />
            User Activity
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Active Users</span>
              <span className="text-sm font-medium">
                {stats.users.active} / {stats.users.total} 
                <span className="text-gray-500 ml-1">
                  ({Math.round((stats.users.active / stats.users.total) * 100)}%)
                </span>
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-green-500 h-2 rounded-full" 
                style={{ width: `${(stats.users.active / stats.users.total) * 100}%` }}
              />
            </div>
          </div>
        </div>

        {/* Aircraft Availability */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-base font-medium text-gray-900 mb-4 flex items-center">
            <Activity className="h-5 w-5 mr-2 text-gray-400" />
            Fleet Status
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Aircraft Available</span>
              <span className="text-sm font-medium">
                {stats.aircraft.available} / {stats.aircraft.total}
                <span className="text-gray-500 ml-1">
                  ({utilizationRate}%)
                </span>
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className={`h-2 rounded-full ${
                  utilizationRate >= 70 ? 'bg-green-500' : 
                  utilizationRate >= 50 ? 'bg-yellow-500' : 'bg-red-500'
                }`}
                style={{ width: `${utilizationRate}%` }}
              />
            </div>
            {utilizationRate < 50 && (
              <div className="flex items-center text-sm text-yellow-600 mt-2">
                <AlertTriangle className="h-4 w-4 mr-1" />
                Low fleet availability
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mission Trends */}
      <div className="mt-6 bg-white rounded-lg shadow p-6">
        <h3 className="text-base font-medium text-gray-900 mb-4 flex items-center">
          <TrendingUp className="h-5 w-5 mr-2 text-gray-400" />
          Mission Activity
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center">
            <p className="text-2xl font-semibold text-gray-900">{stats.missions.total}</p>
            <p className="text-sm text-gray-600">Total Missions</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-semibold text-green-600">{stats.missions.active}</p>
            <p className="text-sm text-gray-600">Active Now</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-semibold text-blue-600">
              {stats.missions.total > 0 ? Math.round(stats.missions.total / 30) : 0}
            </p>
            <p className="text-sm text-gray-600">Avg per Day</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-semibold text-purple-600">
              {stats.aircraft.total > 0 && stats.missions.total > 0 
                ? Math.round(stats.missions.total / stats.aircraft.total)
                : 0
              }
            </p>
            <p className="text-sm text-gray-600">Per Aircraft</p>
          </div>
        </div>
      </div>
    </div>
  );
}